import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { Button } from './ui/button';
import LeadCaptureModal from './LeadCaptureModal';
import { heroData, statsData } from '../data/mock';
import { useCountUp } from '../hooks/useCountUp';

// Slideshow images for the hero section
const slideshowImages = [
  "https://customer-assets.emergentagent.com/job_brand-story-hub-9/artifacts/9y1yoezo_IMG_9498.JPEG",
  "https://customer-assets.emergentagent.com/job_brand-story-hub-9/artifacts/9fhfe81j_maison%20spot%2012.jpeg",
  "https://customer-assets.emergentagent.com/job_brand-story-hub-9/artifacts/nc7kl7w4_led%20%2890%29.JPG",
  "https://customer-assets.emergentagent.com/job_brand-story-hub-9/artifacts/t4j4788y_3d000430-9fa1-4348-b5f7-6bb115f2aec5_enhanced.jpg",
  "https://customer-assets.emergentagent.com/job_brand-story-hub-9/artifacts/qv00hgz2_led%20%2841%29.JPG",
  "https://customer-assets.emergentagent.com/job_brand-story-hub-9/artifacts/oewbcsyx_led%20%2876%29.JPG",
];

const StatItem = ({ value, label, index }) => {
  const numericValue = parseInt(value.replace(/[^0-9]/g, ''), 10);
  const suffix = value.replace(/[0-9]/g, '');
  const { count, ref } = useCountUp(numericValue, 2000 + index * 200);

  return (
    <div ref={ref} data-testid={`stat-${index}`} className="text-center">
      <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground tracking-tight tabular-nums">
        {count}{suffix}
      </div>
      <div className="text-sm text-muted-foreground mt-1.5 tracking-wide">{label}</div>
    </div>
  );
};

const Hero = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Auto-advance slideshow
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % slideshowImages.length);
    }, 3000); // Change image every 3 seconds
    return () => clearInterval(interval);
  }, []);

  return (
    <section data-testid="hero-section" className="relative pt-24 pb-16 md:pt-32 md:pb-24 lg:pt-36 lg:pb-28 bg-background overflow-hidden">
      {/* Ambient glow */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/4 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-primary/5 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/4 pointer-events-none" />
      
      {/* Grid pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border)/0.3)_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--border)/0.3)_1px,transparent_1px)] bg-[size:64px_64px]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">
          {/* Left Content */}
          <div className="lg:col-span-7 space-y-6 md:space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full">
              <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
              <span className="text-xs font-semibold tracking-widest uppercase text-primary">Certified Illumidel Dealer</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-[1.05] tracking-tight">
              {heroData.headline.split(' ').slice(0, 2).join(' ')}{' '}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary/60">
                {heroData.headline.split(' ').slice(2).join(' ')}
              </span>
            </h1>
            
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-xl font-light">
              {heroData.subheadline}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-2">
              <Button 
                onClick={() => setIsModalOpen(true)} 
                size="lg" 
                data-testid="hero-get-started-btn"
                className="text-base px-8 rounded-full shadow-lg shadow-primary/25 hover:shadow-primary/40 hover:scale-105 active:scale-95 transition-all duration-300"
              >
                {heroData.ctaText}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Right Visual - Photo Slideshow */}
          <div className="lg:col-span-5 relative flex justify-center">
            <div className="relative w-full max-w-sm lg:max-w-none">
              {/* Main Slideshow */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-square" data-testid="hero-image">
                {slideshowImages.map((src, index) => (
                  <img
                    key={`hero-slide-${index}`}
                    src={src}
                    alt={`Illumidel lighting showcase ${index + 1}`}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                      index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                    }`}
                  />
                ))}
                {/* Slideshow indicators */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {slideshowImages.map((_, index) => (
                    <button
                      key={`hero-indicator-${index}`}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        index === currentImageIndex 
                          ? 'bg-white w-6' 
                          : 'bg-white/50 hover:bg-white/75'
                      }`}
                    />
                  ))}
                </div>
              </div>
              {/* Photo Credits */}
              <p className="text-xs text-muted-foreground/60 text-center mt-3">Photo credits: Illumidel</p>

              {/* Floating stat cards */}
              <div className="absolute -bottom-4 -left-4 sm:-left-8 bg-card/90 backdrop-blur-xl border border-border/50 rounded-2xl p-3 sm:p-4 shadow-xl z-10">
                <div className="text-xl sm:text-2xl font-bold text-foreground">12 Year</div>
                <div className="text-xs text-muted-foreground">Light Warranty</div>
              </div>
              <div className="absolute -top-2 -right-2 sm:-right-6 bg-card/90 backdrop-blur-xl border border-border/50 rounded-2xl p-3 sm:p-4 shadow-xl z-10">
                <div className="text-xl sm:text-2xl font-bold text-primary">500+</div>
                <div className="text-xs text-muted-foreground">Projects Done</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <LeadCaptureModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </section>
  );
};

export default Hero;
